import React, { useEffect, useMemo, useState } from 'react';
import { 
  Table, 
  Button, 
  Input, 
  Select, 
  Space, 
  Typography,
  Row,
  Col,
  Card
} from 'antd';
import { 
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { adminTextbookApi } from '@/services/api';
import { message } from 'antd';
import AdminLayout from '../../../components/Layouts/AdminLayout/AdminLayout';

const { Title } = Typography;
const { Option } = Select;

interface TextbookData {
  id: string | number;
  type?: string;
  name?: string;
  registrant?: string;
  registrationDate?: string;
}

const AdminTextbookList: React.FC = () => {
  const navigate = useNavigate();
  const [searchType, setSearchType] = useState<string>('');
  const [searchName, setSearchName] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [textbooks, setTextbooks] = useState<TextbookData[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  const loadList = async () => {
    setLoading(true);
    try {
      const res = await adminTextbookApi.list({ q: searchName || '', category: searchType || '', page: page - 1, size: pageSize });
      const data = res?.data || res;
      const content = data?.content || data?.data?.content || [];
      const totalElements = data?.totalElements || data?.data?.totalElements || 0;
      setTextbooks(content);
      setTotal(totalElements);
    } catch (e) {
      message.error('교재 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadList();
  }, [searchName, searchType, page, pageSize]);

  const typeOptions = [
    { value: '', label: '전체' },
    { value: '파닉스데이', label: '파닉스데이' },
    { value: '스타터_파닉스', label: '스타터 파닉스' },
    { value: '스타터_뉘앙스', label: '스타터 뉘앙스' },
    { value: '스타터_스트럭처', label: '스타터 스트럭처' },
    { value: '에세이', label: '에세이' },
    { value: '통번역', label: '통번역' },
    { value: '리딩북', label: '리딩북' }
  ];

  const filteredTextbooks = useMemo(() => textbooks, [textbooks]);

  const handleAddTextbook = () => {
    navigate('/admin/textbook/add');
  };

  const handleEditTextbook = (id: string | number) => {
    navigate(`/admin/textbook/${String(id)}`);
  };

  const handleDeleteTextbook = (id: string) => {
    // 삭제 로직 구현
    console.log('교재 삭제:', id);
  };

  // 서버 페이징 사용, 클라이언트 필터 제거

  const columns = [
    {
      title: '순번',
      dataIndex: 'id',
      key: 'id',
      render: (_: any, __: any, index: number) => (total > 0 ? (total - ((page - 1) * pageSize + index)) : (index + 1)),
      width: 80
    },
    {
      title: '유형',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => {
        const option = typeOptions.find(opt => opt.value === type);
        return option ? option.label : type;
      }
    },
    {
      title: '교재명',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: '등록자',
      dataIndex: 'registrant',
      key: 'registrant'
    },
    {
      title: '등록일',
      dataIndex: 'registrationDate',
      key: 'registrationDate',
      render: (val: any) => {
        if (!val) return '';
        const str = String(val);
        const m = str.match(/^\d{4}-\d{2}-\d{2}/);
        if (m) return m[0];
        const d = new Date(str);
        if (!Number.isNaN(d.getTime())) {
          const y = d.getFullYear();
          const mm = String(d.getMonth() + 1).padStart(2, '0');
          const dd = String(d.getDate()).padStart(2, '0');
          return `${y}-${mm}-${dd}`;
        }
        return str;
      }
    }
  ];

  return (
    <AdminLayout>
      <div style={{ padding: '24px' }}>
        {/* 헤더 */}
        <div style={{ marginBottom: 24 }}>
          <Title level={2}>교재리스트</Title>
        </div>

        {/* 검색 영역 */}
        
          <Row gutter={16} align="bottom" className='mb-4'>
          <Col span={6}>
              <label className="block mb-2 text-sm font-medium">교재명</label>
              <Input
                placeholder="교재명을 입력하세요"
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                prefix={<SearchOutlined />}
                size='large'
              />
            </Col>
            <Col span={4}>
              <label className="block mb-2 text-sm font-medium">유형</label>
              <Select
                placeholder="유형을 선택하세요"
                value={searchType}
                onChange={setSearchType}
                allowClear
                style={{ width: '100%' }}
                size='large'
              >
                {typeOptions.map(option => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Col>
          
            <Col span={14} style={{ textAlign: 'right' }}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddTextbook}
                size="large"
              >
                교재 등록
              </Button>
            </Col>
          </Row>

        {/* 테이블 */}
        <Card>
          <Table
            columns={columns}
            dataSource={filteredTextbooks}
            rowKey="id"
            loading={loading}
            onRow={(record) => ({
              onClick: () => handleEditTextbook(record.id),
              style: { cursor: 'pointer' }
            })}
            pagination={{
              total: total,
              current: page,
              pageSize: pageSize,
              onChange: (p, ps) => { setPage(p); setPageSize(ps || 10); },
              showTotal: (t, range) => `${range[0]}-${range[1]} / 총 ${t}개`
            }}
          />
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminTextbookList; 